import {
  STATION_LABELS,
  type CustomerConversation,
  type KitchenSnapshot,
  type SessionStats,
  type StationId,
  type UiIntent,
} from '../game/types';
import './ui.css';

type PrepRow = 'chicken' | 'rice' | 'vegetables' | 'oven';

const STATION_ICONS: Record<StationId, string> = {
  'chicken-crates': '📦',
  'chicken-board': '🔪',
  marinade: '🥣',
  fridge: '❄️',
  'rice-bags': '🌾',
  sink: '💧',
  'veg-basket': '🥔',
  'veg-board': '🥕',
  oven: '🔥',
  serve: '🫴',
};

const PREP_LABELS: Record<PrepRow, string> = {
  chicken: 'الدجاج',
  rice: 'الرز',
  vegetables: 'الخضار',
  oven: 'الفرن',
};

const PREP_ICONS: Record<PrepRow, string> = {
  chicken: '🍗',
  rice: '🍚',
  vegetables: '🥔',
  oven: '🔥',
};

function escapeAttribute(value: string): string {
  return value.replace(/[&<>"]/g, (character) => {
    const entities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
    };
    return entities[character];
  });
}

export class GameUI {
  private readonly host: HTMLElement;
  private readonly root: HTMLElement;
  private readonly abortController = new AbortController();
  private readonly stationButtons: HTMLButtonElement[] = [];
  private readonly prepRows = new Map<PrepRow, HTMLElement>();
  private readonly onIntent: (intent: UiIntent) => void;
  private flashTimer?: number;
  private previousOrderId = -1;
  private previousOrderStatus = '';
  private previousToast = '';
  private phoneView: 'list' | 'thread' = 'list';
  private selectedConversationId?: number;
  private conversationSignature = '';
  private sessionSummaryDismissed = false;
  private lastSnapshot?: KitchenSnapshot;

  constructor(onIntent: (intent: UiIntent) => void) {
    const host = document.querySelector<HTMLElement>('#game-ui');
    if (!host) throw new Error('Missing #game-ui element.');

    this.host = host;
    this.onIntent = onIntent;
    this.host.replaceChildren();
    this.host.classList.add('mk-ui-host');

    this.root = document.createElement('div');
    this.root.className = 'mk-ui';
    this.root.dir = 'rtl';
    this.root.innerHTML = this.createMarkup();
    this.host.append(this.root);

    for (const button of this.root.querySelectorAll<HTMLButtonElement>('[data-station]')) {
      this.stationButtons.push(button);
    }
    for (const row of this.root.querySelectorAll<HTMLElement>('[data-prep-row]')) {
      this.prepRows.set(row.dataset.prepRow as PrepRow, row);
    }

    this.root.addEventListener(
      'click',
      (event) => {
        const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
        if (!button || button.disabled) return;

        const conversationId = Number(button.dataset.conversationId);
        if (Number.isFinite(conversationId)) {
          this.selectedConversationId = conversationId;
          this.phoneView = 'thread';
          if (this.lastSnapshot) this.updatePhone(this.lastSnapshot, true);
          return;
        }

        const durationMinutes = Number(button.dataset.durationMinutes);
        if (Number.isFinite(durationMinutes)) {
          this.onIntent({ type: 'set-session-duration', minutes: durationMinutes });
          return;
        }

        const station = button.dataset.station as StationId | undefined;
        if (station) {
          this.onIntent({ type: 'station', station });
          return;
        }

        switch (button.dataset.uiAction) {
          case 'pause':
            this.onIntent({ type: 'pause-toggle' });
            break;
          case 'mute':
            this.onIntent({ type: 'mute-toggle' });
            break;
          case 'settings-show':
            this.onIntent({ type: 'settings-show' });
            break;
          case 'settings-hide':
            this.onIntent({ type: 'settings-hide' });
            break;
          case 'new-session':
            this.phoneView = 'list';
            this.selectedConversationId = undefined;
            this.sessionSummaryDismissed = false;
            this.onIntent({ type: 'new-session' });
            break;
          case 'phone-back':
            this.phoneView = 'list';
            this.selectedConversationId = undefined;
            if (this.lastSnapshot) this.updatePhone(this.lastSnapshot, true);
            break;
          case 'reviews-show':
            this.phoneView = 'list';
            this.selectedConversationId = undefined;
            this.sessionSummaryDismissed = true;
            if (this.lastSnapshot) {
              this.updatePhone(this.lastSnapshot, true);
              this.updateOverlay(this.lastSnapshot);
            }
            break;
          case 'session-summary-show':
            this.sessionSummaryDismissed = false;
            if (this.lastSnapshot) this.updateOverlay(this.lastSnapshot);
            break;
          case 'restart':
            this.onIntent({ type: 'restart' });
            break;
          case 'tutorial-dismiss':
            this.onIntent({ type: 'tutorial-dismiss' });
            break;
          case 'tutorial-show':
            this.onIntent({ type: 'tutorial-show' });
            break;
        }
      },
      { signal: this.abortController.signal },
    );
  }

  render(snapshot: KitchenSnapshot): void {
    this.lastSnapshot = snapshot;
    const remaining = Math.max(0, snapshot.order.remaining);
    const total = Math.max(1, snapshot.order.total || 90);
    const timeRatio = Math.min(1, remaining / total);
    const urgent = remaining <= Math.min(15, total * 0.2) && snapshot.order.status === 'active';
    const warning = !urgent && remaining <= total / 2 && snapshot.order.status === 'active';

    this.root.style.setProperty('--mk-order-progress', `${timeRatio * 100}%`);
    this.root.style.setProperty('--mk-reputation-progress', `${Math.max(0, Math.min(100, snapshot.reputation))}%`);
    this.root.dataset.timerTone = urgent ? 'danger' : warning ? 'warning' : 'normal';

    const sessionRemaining = Math.max(0, snapshot.session?.remaining ?? 600);
    this.root.dataset.sessionTone = sessionRemaining <= 60 ? 'danger' : sessionRemaining <= 180 ? 'warning' : 'normal';

    this.setText('[data-value="timer"]', this.formatTime(remaining));
    this.setText('[data-value="session-timer"]', this.formatTime(sessionRemaining));
    this.setText('[data-value="score"]', this.formatNumber(snapshot.score));
    this.setText('[data-value="reputation"]', this.formatNumber(snapshot.reputation));
    this.setText('[data-value="combo"]', `×${this.formatNumber(snapshot.combo)}`);
    this.setText('[data-value="order-id"]', `طلب #${this.formatNumber(snapshot.order.id)}`);
    this.setText('[data-value="chickens"]', this.formatNumber(snapshot.order.chickens));
    this.setText('[data-value="hint"]', snapshot.hint || `الخطوة التالية: ${STATION_LABELS[snapshot.nextStation]}`);
    this.setText('[data-value="next-station"]', STATION_LABELS[snapshot.nextStation]);
    this.updatePhone(snapshot);
    this.updatePrep(snapshot);
    this.updateControls(snapshot);
    this.updateOverlay(snapshot);

    const orderChanged = this.previousOrderId !== snapshot.order.id;
    const statusChanged = this.previousOrderStatus !== snapshot.order.status;
    if (orderChanged && this.previousOrderId >= 0) {
      this.phoneView = 'list';
      this.selectedConversationId = undefined;
      this.conversationSignature = '';
      this.updatePhone(snapshot, true);
      this.flash('وصل طلب جديد على الجوال');
    }
    if (!orderChanged && statusChanged && snapshot.order.status === 'delivered') {
      this.flash(snapshot.order.reaction || 'تم التسليم! عميل مبسوط وسلسلة أقوى');
    }
    this.previousOrderId = snapshot.order.id;
    this.previousOrderStatus = snapshot.order.status;

    if (!snapshot.session?.ended) this.sessionSummaryDismissed = false;

    if (snapshot.toast && snapshot.toast !== this.previousToast) this.flash(snapshot.toast);
    this.previousToast = snapshot.toast ?? '';
  }

  flash(message: string): void {
    if (!message) return;
    const toast = this.getElement<HTMLElement>('[data-ui="toast"]');
    toast.textContent = message;
    toast.classList.remove('mk-toast--visible');
    // Restart the entrance motion when consecutive events share the same text.
    void toast.offsetWidth;
    toast.classList.add('mk-toast--visible');
    window.clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => toast.classList.remove('mk-toast--visible'), 2600);
  }

  dispose(): void {
    window.clearTimeout(this.flashTimer);
    this.abortController.abort();
    this.root.remove();
    this.host.classList.remove('mk-ui-host');
  }

  private createMarkup(): string {
    const stationButtons = (Object.keys(STATION_LABELS) as StationId[])
      .map(
        (station) => `
          <button class="mk-station-button" type="button" data-station="${escapeAttribute(station)}"
            aria-label="${escapeAttribute(STATION_LABELS[station])}" title="${escapeAttribute(STATION_LABELS[station])}">
            <span class="mk-station-button__icon" aria-hidden="true">${STATION_ICONS[station]}</span>
            <span>${STATION_LABELS[station]}</span>
          </button>`,
      )
      .join('');

    const prepRows = (Object.keys(PREP_LABELS) as PrepRow[])
      .map(
        (row) => `
          <li class="mk-prep-row" data-prep-row="${row}">
            <span class="mk-prep-row__icon" aria-hidden="true">${PREP_ICONS[row]}</span>
            <span class="mk-prep-row__copy">
              <strong>${PREP_LABELS[row]}</strong>
              <small data-prep-status>بانتظار البدء</small>
            </span>
            <span class="mk-prep-row__tick" aria-hidden="true">✓</span>
          </li>`,
      )
      .join('');

    return `
      <div class="mk-layout">
      <header class="mk-topbar" aria-label="حالة الجولة">
        <div class="mk-brand-lockup">
          <img src="/assets/brand/mandy-kaif-logo.jpg" alt="" />
          <span><strong>وصل مندي كايف</strong><small>أصالة المندي… بطعم يحكي الفخر</small></span>
        </div>

        <div class="mk-metrics" aria-label="نتيجة اللعب">
          <div class="mk-metric mk-metric--session"><span>الجولة</span><strong data-value="session-timer">10:00</strong></div>
          <div class="mk-metric mk-metric--score"><span>النقاط</span><strong data-value="score">0</strong></div>
          <div class="mk-metric mk-metric--reputation">
            <span>السمعة</span><strong data-value="reputation">100</strong>
            <i aria-hidden="true"><b></b></i>
          </div>
          <div class="mk-metric mk-metric--combo"><span>السلسلة</span><strong data-value="combo">×0</strong></div>
        </div>

        <div class="mk-quick-actions" aria-label="إعدادات اللعبة">
          <button type="button" data-ui-action="mute" aria-label="كتم الأصوات" title="الصوت">
            <span data-icon="sound-on" aria-hidden="true">🔊</span>
            <span data-icon="sound-off" aria-hidden="true">🔇</span>
          </button>
          <button type="button" data-ui-action="tutorial-show" aria-label="عرض طريقة اللعب" title="المساعدة">?</button>
          <button type="button" data-ui-action="settings-show" aria-label="إعدادات الجولة" title="الإعدادات">⚙</button>
          <button type="button" data-ui-action="pause" aria-label="إيقاف اللعبة مؤقتًا" title="إيقاف مؤقت">Ⅱ</button>
        </div>
      </header>

      <div class="mk-side-column" role="group" aria-label="لوحة الطلبات والتحضير">
      <aside class="mk-phone" aria-label="جوال الطلبات">
        <div class="mk-phone__speaker" aria-hidden="true"></div>
        <div class="mk-phone__screen">
          <header class="mk-chat-head">
            <button class="mk-chat-back" type="button" data-ui-action="phone-back" aria-label="العودة إلى المحادثات" hidden>‹</button>
            <div class="mk-chat-head__avatar"><img src="/assets/brand/mandy-kaif-logo.jpg" alt="" /></div>
            <div><strong data-value="phone-title">طلبات مندي كايف</strong><small data-value="phone-subtitle">الأحدث أولًا</small></div>
            <span class="mk-chat-head__dots" aria-hidden="true">•••</span>
          </header>

          <div class="mk-order-clock" data-ui="order-clock">
            <div class="mk-order-clock__meta">
              <span data-value="order-id">طلب #1</span>
              <strong data-value="timer">01:30</strong>
            </div>
            <div class="mk-order-clock__track" role="progressbar" aria-label="الوقت المتبقي للطلب"><i></i></div>
          </div>

          <div class="mk-conversation-list" data-ui="conversation-list" aria-label="المحادثات" aria-live="polite"></div>

          <div class="mk-chat-body mk-chat-thread" data-ui="conversation-thread" aria-live="polite" hidden>
            <div class="mk-chat-thread__messages" data-ui="conversation-messages"></div>
            <div class="mk-thread-review" data-ui="thread-review" hidden></div>
          </div>

          <footer class="mk-order-summary" data-ui="order-summary">
            <span class="mk-chicken-count"><b aria-hidden="true">🍗</b><strong data-value="chickens">1</strong><small>دجاج</small></span>
            <span>كل دجاجة تُقطع نصفين</span>
          </footer>

          <button class="mk-session-recap-return" type="button" data-ui-action="session-summary-show" hidden>
            عرض ملخص الجولة
          </button>
        </div>
        <div class="mk-phone__home" aria-hidden="true"></div>
      </aside>

      <section class="mk-prep" aria-label="قائمة التحضير">
        <header>
          <div><small>المحطة التالية</small><strong data-value="next-station">كراتين الدجاج</strong></div>
          <span class="mk-next-pulse" aria-hidden="true"></span>
        </header>
        <p data-value="hint">ابدأ بتجهيز طلب العميل</p>
        <ol>${prepRows}</ol>
      </section>
      </div>

      <nav class="mk-station-dock" aria-label="محطات المطبخ">
        ${stationButtons}
      </nav>

      <div class="mk-toast" data-ui="toast" role="status" aria-live="polite"></div>

      <div class="mk-overlay" data-ui="overlay" hidden>
        <section class="mk-dialog mk-dialog--tutorial" data-dialog="tutorial" role="dialog" aria-modal="true" aria-labelledby="mk-tutorial-title" hidden>
          <div class="mk-dialog__art mk-dialog__art--oven">
            <img src="/assets/generated/mandi-oven-stylized.png" alt="فرن مندي كرتوني" />
          </div>
          <div class="mk-dialog__body">
            <span class="mk-dialog__eyebrow">وردية الشيف</span>
            <h2 id="mk-tutorial-title">وصل الطلب قبل ما يبرد!</h2>
            <div class="mk-tutorial-steps">
              <p><b>1</b><span><strong>جهّز المكونات</strong>قطّع الدجاج والخضار واغسل الرز ثلاث مرات.</span></p>
              <p><b>2</b><span><strong>التزم بالوقت</strong>انتظر التتبيل والتبريد، ثم حمّل فرن المندي.</span></p>
              <p><b>3</b><span><strong>سلّم المندوب</strong>كلما أسرعت زادت السمعة والسلسلة.</span></p>
            </div>
            <button class="mk-primary-action" type="button" data-ui-action="tutorial-dismiss">يلا نطبخ</button>
          </div>
        </section>

        <section class="mk-dialog mk-dialog--paused" data-dialog="paused" role="dialog" aria-modal="true" aria-labelledby="mk-paused-title" hidden>
          <span class="mk-dialog__symbol" aria-hidden="true">Ⅱ</span>
          <span class="mk-dialog__eyebrow">المطبخ متوقف</span>
          <h2 id="mk-paused-title">خذ نفس… الوقت واقف معك</h2>
          <button class="mk-primary-action" type="button" data-ui-action="pause">كمّل الطلب</button>
          <button class="mk-secondary-action" type="button" data-ui-action="restart">إعادة الجولة</button>
        </section>

        <section class="mk-dialog mk-dialog--failed" data-dialog="failed" role="dialog" aria-modal="true" aria-labelledby="mk-failed-title" hidden>
          <span class="mk-dialog__symbol mk-dialog__symbol--danger" aria-hidden="true">!</span>
          <span class="mk-dialog__eyebrow">انتهى وقت الطلب</span>
          <h2 id="mk-failed-title">العميل زعل</h2>
          <blockquote data-value="fail-reaction">أنا المخطي اللي أطلب من عندكم… التأخير ما ينفع.</blockquote>
          <button class="mk-primary-action" type="button" data-ui-action="restart">نعوّضها بطلب أسرع</button>
        </section>

        <section class="mk-dialog mk-dialog--settings" data-dialog="settings" role="dialog" aria-modal="true" aria-labelledby="mk-settings-title" hidden>
          <div class="mk-settings-head">
            <div>
              <span class="mk-dialog__eyebrow">ضبط الوردية</span>
              <h2 id="mk-settings-title">إعدادات الجولة</h2>
            </div>
            <button class="mk-dialog-close" type="button" data-ui-action="settings-hide" aria-label="إغلاق الإعدادات">×</button>
          </div>
          <p>اختر مدة الجولة القادمة. تبدأ المدة المختارة عند الضغط على «جولة جديدة».</p>
          <div class="mk-duration-picker" role="group" aria-label="مدة الجولة بالدقائق">
            <button type="button" data-duration-minutes="5">5<small>دقائق</small></button>
            <button type="button" data-duration-minutes="10">10<small>دقائق</small></button>
            <button type="button" data-duration-minutes="15">15<small>دقيقة</small></button>
            <button type="button" data-duration-minutes="20">20<small>دقيقة</small></button>
          </div>
          <button class="mk-settings-sound" type="button" data-ui-action="mute">
            <span data-settings-sound-icon aria-hidden="true">🔊</span>
            <span><strong data-settings-sound-label>الأصوات تعمل</strong><small>مؤثرات المطبخ والتنبيهات بلا موسيقى</small></span>
            <i aria-hidden="true"></i>
          </button>
          <div class="mk-settings-actions">
            <button class="mk-primary-action" type="button" data-ui-action="new-session">ابدأ جولة جديدة</button>
            <button class="mk-secondary-action" type="button" data-ui-action="settings-hide">رجوع</button>
          </div>
        </section>

        <section class="mk-dialog mk-dialog--session-end" data-dialog="session-end" role="dialog" aria-modal="true" aria-labelledby="mk-session-end-title" hidden>
          <span class="mk-dialog__eyebrow">انتهت الوردية</span>
          <h2 id="mk-session-end-title">حصيلة مطبخك</h2>
          <div class="mk-final-score"><span>النقاط النهائية</span><strong data-stat="score">0</strong><small>السمعة <b data-stat="reputation">0</b></small></div>
          <div class="mk-session-stats">
            <div><span aria-hidden="true">✓</span><small>طلبات مسلّمة</small><strong data-stat="delivered">0</strong></div>
            <div class="mk-stat--negative"><span aria-hidden="true">×</span><small>طلبات فائتة</small><strong data-stat="expired">0</strong></div>
            <div><span aria-hidden="true">◷</span><small>متوسط التسليم</small><strong data-stat="average">00:00</strong></div>
            <div><span aria-hidden="true">⚡</span><small>أفضل تسليم</small><strong data-stat="best">—</strong></div>
            <div><span aria-hidden="true">★</span><small>آراء إيجابية</small><strong data-stat="positive">0</strong></div>
            <div class="mk-stat--negative"><span aria-hidden="true">!</span><small>آراء سلبية</small><strong data-stat="negative">0</strong></div>
          </div>
          <div class="mk-session-end-actions">
            <button class="mk-primary-action" type="button" data-ui-action="new-session">جولة جديدة</button>
            <button class="mk-secondary-action" type="button" data-ui-action="reviews-show">تصفّح آراء العملاء</button>
            <button class="mk-text-action" type="button" data-ui-action="settings-show">تغيير مدة الجولة</button>
          </div>
        </section>

        <section class="mk-dialog mk-dialog--ready" data-dialog="ready" role="dialog" aria-modal="true" aria-labelledby="mk-ready-title" hidden>
          <div class="mk-dialog__art mk-dialog__art--platter">
            <img src="/assets/generated/mandi-platter-stylized.png" alt="صحن مندي بدجاجة كاملة مقسومة نصفين وجاهزة للتسليم" />
          </div>
          <div class="mk-dialog__body">
            <span class="mk-dialog__eyebrow">حار وتوّه طالع</span>
            <h2 id="mk-ready-title">الصحن جاهز للمندوب</h2>
            <p>سلّمه الآن قبل ما ينقص رضا العميل.</p>
            <button class="mk-primary-action" type="button" data-station="serve">سلّم المندوب</button>
          </div>
        </section>
      </div>
      </div>
    `;
  }

  private updatePhone(snapshot: KitchenSnapshot, force = false): void {
    const conversations = this.getConversations(snapshot);
    const selected =
      conversations.find((conversation) => conversation.orderId === this.selectedConversationId) ?? conversations[0];
    if (this.phoneView === 'thread' && selected) this.selectedConversationId = selected.orderId;

    const signature = JSON.stringify({
      view: this.phoneView,
      selected: this.selectedConversationId,
      ended: snapshot.session?.ended ?? false,
      dismissed: this.sessionSummaryDismissed,
      conversations: conversations.map((conversation) => ({
        orderId: conversation.orderId,
        status: conversation.status,
        rating: conversation.rating,
        deliverySeconds: conversation.deliverySeconds,
        messages: conversation.messages.map((message) => [message.id, message.text, message.sender]),
      })),
    });
    if (!force && signature === this.conversationSignature) return;
    this.conversationSignature = signature;

    const list = this.getElement<HTMLElement>('[data-ui="conversation-list"]');
    const thread = this.getElement<HTMLElement>('[data-ui="conversation-thread"]');
    const back = this.getElement<HTMLButtonElement>('[data-ui-action="phone-back"]');
    const clock = this.getElement<HTMLElement>('[data-ui="order-clock"]');
    const summary = this.getElement<HTMLElement>('[data-ui="order-summary"]');
    const recap = this.getElement<HTMLButtonElement>('[data-ui-action="session-summary-show"]');
    const showingThread = this.phoneView === 'thread' && Boolean(selected);

    list.hidden = showingThread;
    thread.hidden = !showingThread;
    back.hidden = !showingThread;
    recap.hidden = !(snapshot.session?.ended && this.sessionSummaryDismissed && !showingThread);

    if (!showingThread) {
      this.setText('[data-value="phone-title"]', 'طلبات مندي كايف');
      this.setText(
        '[data-value="phone-subtitle"]',
        conversations.length ? `${this.formatNumber(conversations.length)} محادثات • الأحدث أولًا` : 'لا توجد محادثات بعد',
      );
      clock.hidden = Boolean(snapshot.session?.ended);
      summary.hidden = Boolean(snapshot.session?.ended);
      this.renderConversationList(list, conversations, snapshot.order.id);
      return;
    }

    this.setText('[data-value="phone-title"]', selected?.customer ?? 'عميل مندي كايف');
    this.setText('[data-value="phone-subtitle"]', selected ? this.conversationSubtitle(selected) : 'سجل الطلب');
    const viewingCurrent = selected?.orderId === snapshot.order.id && !snapshot.session?.ended;
    clock.hidden = !viewingCurrent;
    summary.hidden = !viewingCurrent;
    if (selected) this.renderConversationThread(selected);
  }

  private renderConversationList(
    container: HTMLElement,
    conversations: CustomerConversation[],
    currentOrderId: number,
  ): void {
    container.replaceChildren();
    if (!conversations.length) {
      const empty = document.createElement('div');
      empty.className = 'mk-conversation-empty';
      empty.innerHTML = '<span aria-hidden="true">💬</span><strong>المحادثات بتظهر هنا</strong><small>ابدأ الجولة ليصل أول طلب.</small>';
      container.append(empty);
      return;
    }

    for (const conversation of conversations) {
      const latestMessage = conversation.messages.at(-1)?.text || this.orderMessage(conversation.chickens);
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'mk-conversation-row';
      row.dataset.conversationId = String(conversation.orderId);
      row.setAttribute('aria-label', `محادثة ${conversation.customer}، ${this.statusLabel(conversation.status)}`);
      if (conversation.orderId === currentOrderId && conversation.status !== 'delivered' && conversation.status !== 'expired') {
        row.classList.add('mk-conversation-row--current');
      }

      const avatar = document.createElement('span');
      avatar.className = 'mk-conversation-avatar';
      avatar.textContent = conversation.customer.trim().charAt(0) || 'م';

      const copy = document.createElement('span');
      copy.className = 'mk-conversation-copy';
      const heading = document.createElement('strong');
      heading.textContent = conversation.customer;
      const preview = document.createElement('small');
      preview.textContent = latestMessage;
      copy.append(heading, preview);

      const meta = document.createElement('span');
      meta.className = 'mk-conversation-meta';
      const orderNumber = document.createElement('small');
      orderNumber.textContent = `#${this.formatNumber(conversation.orderId)}`;
      const badge = document.createElement('b');
      badge.className = `mk-conversation-status mk-conversation-status--${conversation.status}`;
      badge.textContent = this.statusLabel(conversation.status);
      meta.append(orderNumber, badge);
      if (conversation.rating) {
        const rating = document.createElement('span');
        rating.className = 'mk-conversation-rating';
        rating.textContent = `${this.formatNumber(conversation.rating)} ★`;
        meta.append(rating);
      }

      row.append(avatar, copy, meta);
      container.append(row);
    }
  }

  private renderConversationThread(conversation: CustomerConversation): void {
    const messagesContainer = this.getElement<HTMLElement>('[data-ui="conversation-messages"]');
    const review = this.getElement<HTMLElement>('[data-ui="thread-review"]');
    messagesContainer.replaceChildren();

    const day = document.createElement('span');
    day.className = 'mk-chat-day';
    day.textContent = `طلب #${this.formatNumber(conversation.orderId)}`;
    messagesContainer.append(day);

    const messages = conversation.messages.length
      ? conversation.messages
      : [
          {
            id: `fallback-${conversation.orderId}`,
            sender: 'customer' as const,
            text: this.orderMessage(conversation.chickens),
            sentAt: 0,
          },
        ];
    for (const message of messages) {
      const bubble = document.createElement('div');
      bubble.className = `mk-bubble ${message.sender === 'restaurant' ? 'mk-bubble--outgoing' : 'mk-bubble--incoming'}`;
      const text = document.createElement('span');
      text.textContent = message.text;
      const sender = document.createElement('small');
      sender.textContent = message.sender === 'restaurant' ? 'مندي كايف ✓✓' : conversation.customer;
      bubble.append(text, sender);
      messagesContainer.append(bubble);
    }

    if (conversation.rating || conversation.status === 'expired' || conversation.deliverySeconds !== undefined) {
      review.hidden = false;
      review.replaceChildren();
      const label = document.createElement('small');
      label.textContent = conversation.status === 'expired' ? 'لم يتم التسليم' : 'تقييم العميل';
      const value = document.createElement('strong');
      value.textContent = conversation.rating
        ? `${'★'.repeat(Math.max(0, Math.min(5, conversation.rating)))}${'☆'.repeat(Math.max(0, 5 - conversation.rating))}`
        : '—';
      review.append(label, value);
      if (conversation.deliverySeconds !== undefined) {
        const delivery = document.createElement('span');
        delivery.textContent = `وصل خلال ${this.formatTime(conversation.deliverySeconds)}`;
        review.append(delivery);
      }
    } else {
      review.hidden = true;
      review.replaceChildren();
    }
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  private getConversations(snapshot: KitchenSnapshot): CustomerConversation[] {
    const conversations = [...(snapshot.conversations ?? [])];
    if (!conversations.some((conversation) => conversation.orderId === snapshot.order.id)) {
      const messages = [
        {
          id: `order-${snapshot.order.id}`,
          sender: 'customer' as const,
          text: this.orderMessage(snapshot.order.chickens),
          sentAt: Number.MAX_SAFE_INTEGER - 1,
        },
        {
          id: `ack-${snapshot.order.id}`,
          sender: 'restaurant' as const,
          text: 'أبشر، طلبك تحت التجهيز 👌',
          sentAt: Number.MAX_SAFE_INTEGER,
        },
      ];
      if (snapshot.order.reaction) {
        messages.push({
          id: `reaction-${snapshot.order.id}`,
          sender: 'customer',
          text: snapshot.order.reaction,
          sentAt: Number.MAX_SAFE_INTEGER,
        });
      }
      conversations.push({
        orderId: snapshot.order.id,
        customer: snapshot.order.customer,
        chickens: snapshot.order.chickens,
        status: snapshot.order.status,
        messages,
      });
    }

    return conversations.sort((left, right) => {
      if (left.orderId === snapshot.order.id) return -1;
      if (right.orderId === snapshot.order.id) return 1;
      const leftTime = Math.max(0, ...left.messages.map((message) => message.sentAt));
      const rightTime = Math.max(0, ...right.messages.map((message) => message.sentAt));
      return rightTime - leftTime || right.orderId - left.orderId;
    });
  }

  private conversationSubtitle(conversation: CustomerConversation): string {
    const rating = conversation.rating ? ` • ${this.formatNumber(conversation.rating)} من 5 ★` : '';
    return `${this.statusLabel(conversation.status)}${rating}`;
  }

  private statusLabel(status: CustomerConversation['status']): string {
    const labels: Record<CustomerConversation['status'], string> = {
      active: 'طلب جديد',
      ready: 'جاهز للتسليم',
      delivered: 'تم التسليم',
      expired: 'فات الطلب',
    };
    return labels[status];
  }

  private updatePrep(snapshot: KitchenSnapshot): void {
    const requestedChickens = Math.max(1, snapshot.order.chickens);
    const chickenReady = Math.max(0, snapshot.chicken.ovenReady);
    let chickenStatus = `${this.formatNumber(chickenReady)}/${this.formatNumber(requestedChickens)} جاهز للفرن`;
    if (chickenReady < requestedChickens) {
      if (snapshot.chicken.chillRemaining > 0) chickenStatus = `تبريد ${this.formatShortSeconds(snapshot.chicken.chillRemaining)}`;
      else if (snapshot.chicken.marinadeRemaining > 0) chickenStatus = `تتبيل ${this.formatShortSeconds(snapshot.chicken.marinadeRemaining)}`;
      else if (snapshot.chicken.chilledCount > chickenReady) chickenStatus = 'انقل الدجاج للفرن';
      else if (snapshot.chicken.marinadeCount > snapshot.chicken.chilledCount) chickenStatus = 'انقل الدجاج للثلاجة';
      else if (snapshot.chicken.cutHalves > snapshot.chicken.marinadeCount * 2) chickenStatus = 'غمّس الدجاج بالتتبيلة';
      else if (snapshot.chicken.onBoard > 0) chickenStatus = 'اقطع كل دجاجة نصفين';
      else if (snapshot.chicken.cratesTaken > 0) chickenStatus = 'طلّع الدجاج على القطّاعة';
      else chickenStatus = `جهّز ${this.chickenPhrase(requestedChickens)}`;
    }
    this.setPrepRow('chicken', chickenStatus, chickenReady >= requestedChickens);

    let riceStatus = 'جهّز كيس الرز';
    if (snapshot.rice.ovenReady) riceStatus = 'جاهز داخل الفرن';
    else if (snapshot.rice.washCooldown > 0) riceStatus = `تصفي الغسلة… ${this.formatShortSeconds(snapshot.rice.washCooldown)}`;
    else if (snapshot.rice.washCount > 0) riceStatus = `غسلة ${this.formatNumber(snapshot.rice.washCount)} من 3`;
    else if (snapshot.rice.bowlFilled) riceStatus = 'ابدأ غسل الرز';
    else if (snapshot.rice.bagPlaced) riceStatus = 'عبّ البادية';
    this.setPrepRow('rice', riceStatus, snapshot.rice.ovenReady);

    let vegetablesStatus = 'انقل الخضار للقطّاعة';
    if (snapshot.vegetables.ovenReady) vegetablesStatus = 'جاهزة داخل الفرن';
    else if (snapshot.vegetables.chopCooldown > 0) vegetablesStatus = `تقطيع… ${this.formatShortSeconds(snapshot.vegetables.chopCooldown)}`;
    else if (snapshot.vegetables.chopCount > 0) vegetablesStatus = `تقطيعة ${this.formatNumber(snapshot.vegetables.chopCount)} من 3`;
    else if (snapshot.vegetables.placed) vegetablesStatus = 'اضغط القطّاعة 3 مرات';
    this.setPrepRow('vegetables', vegetablesStatus, snapshot.vegetables.ovenReady);

    let ovenStatus = 'بانتظار المكونات';
    if (snapshot.oven.platterReady) ovenStatus = 'الصحن جاهز للتسليم';
    else if (snapshot.oven.cooking) ovenStatus = `يطبخ… ${this.formatShortSeconds(snapshot.oven.remaining)}`;
    else if (snapshot.oven.loaded) ovenStatus = 'ابدأ طبخ المندي';
    this.setPrepRow('oven', ovenStatus, snapshot.oven.platterReady);
  }

  private updateControls(snapshot: KitchenSnapshot): void {
    for (const button of this.stationButtons) {
      const station = button.dataset.station as StationId;
      const active = station === snapshot.nextStation;
      button.classList.toggle('mk-station-button--active', active);
      button.setAttribute('aria-current', active ? 'step' : 'false');
    }

    for (const muteButton of this.root.querySelectorAll<HTMLButtonElement>('[data-ui-action="mute"]')) {
      muteButton.classList.toggle('is-active', snapshot.muted);
      muteButton.setAttribute('aria-pressed', String(snapshot.muted));
      muteButton.setAttribute('aria-label', snapshot.muted ? 'تشغيل الأصوات' : 'كتم الأصوات');
    }
    this.setText('[data-settings-sound-label]', snapshot.muted ? 'الأصوات متوقفة' : 'الأصوات تعمل');
    this.setText('[data-settings-sound-icon]', snapshot.muted ? '🔇' : '🔊');

    const pauseButton = this.getElement<HTMLButtonElement>('[data-ui-action="pause"]');
    pauseButton.setAttribute('aria-pressed', String(snapshot.paused));
    pauseButton.disabled = Boolean(snapshot.session?.ended || snapshot.session?.settingsVisible);

    const settingsButton = this.getElement<HTMLButtonElement>('.mk-topbar [data-ui-action="settings-show"]');
    settingsButton.setAttribute('aria-expanded', String(Boolean(snapshot.session?.settingsVisible)));

    const configuredMinutes = Math.round((snapshot.session?.configuredDuration ?? 600) / 60);
    for (const durationButton of this.root.querySelectorAll<HTMLButtonElement>('[data-duration-minutes]')) {
      const selected = Number(durationButton.dataset.durationMinutes) === configuredMinutes;
      durationButton.classList.toggle('is-selected', selected);
      durationButton.setAttribute('aria-pressed', String(selected));
    }
  }

  private updateOverlay(snapshot: KitchenSnapshot): void {
    const overlay = this.getElement<HTMLElement>('[data-ui="overlay"]');
    let activeDialog: 'tutorial' | 'failed' | 'paused' | 'ready' | 'settings' | 'session-end' | undefined;
    if (snapshot.session?.settingsVisible) activeDialog = 'settings';
    else if (snapshot.session?.ended && !this.sessionSummaryDismissed) activeDialog = 'session-end';
    else if (snapshot.session?.ended) activeDialog = undefined;
    else if (snapshot.tutorialVisible) activeDialog = 'tutorial';
    else if (snapshot.order.status === 'expired') activeDialog = 'failed';
    else if (snapshot.paused) activeDialog = 'paused';
    else if (snapshot.oven.platterReady || snapshot.order.status === 'ready') activeDialog = 'ready';

    overlay.hidden = !activeDialog;
    overlay.dataset.active = activeDialog ?? '';
    for (const dialog of overlay.querySelectorAll<HTMLElement>('[data-dialog]')) {
      dialog.hidden = dialog.dataset.dialog !== activeDialog;
    }

    if (activeDialog === 'failed') {
      this.setText('[data-value="fail-reaction"]', snapshot.order.reaction || 'أنا المخطي اللي أطلب من عندكم… التأخير ما ينفع.');
    }
    if (activeDialog === 'session-end') this.updateSessionStats(snapshot);
  }

  private updateSessionStats(snapshot: KitchenSnapshot): void {
    const stats = snapshot.session?.stats ?? this.deriveSessionStats(snapshot);
    this.setText('[data-stat="score"]', this.formatNumber(stats.finalScore));
    this.setText('[data-stat="reputation"]', this.formatNumber(stats.finalReputation));
    this.setText('[data-stat="delivered"]', this.formatNumber(stats.deliveredOrders));
    this.setText('[data-stat="expired"]', this.formatNumber(stats.expiredOrders));
    this.setText('[data-stat="average"]', stats.deliveredOrders ? this.formatTime(stats.averageDeliverySeconds) : '—');
    this.setText('[data-stat="best"]', stats.bestDeliverySeconds === undefined ? '—' : this.formatTime(stats.bestDeliverySeconds));
    this.setText('[data-stat="positive"]', this.formatNumber(stats.positiveReviews));
    this.setText('[data-stat="negative"]', this.formatNumber(stats.negativeReviews));
  }

  private deriveSessionStats(snapshot: KitchenSnapshot): SessionStats {
    const conversations = snapshot.conversations ?? [];
    const delivered = conversations.filter((conversation) => conversation.status === 'delivered');
    const expired = conversations.filter((conversation) => conversation.status === 'expired');
    const deliveryTimes = delivered
      .map((conversation) => conversation.deliverySeconds)
      .filter((seconds): seconds is number => seconds !== undefined);
    return {
      totalOrders: delivered.length + expired.length,
      deliveredOrders: delivered.length,
      expiredOrders: expired.length,
      averageDeliverySeconds: deliveryTimes.length
        ? deliveryTimes.reduce((total, seconds) => total + seconds, 0) / deliveryTimes.length
        : 0,
      bestDeliverySeconds: deliveryTimes.length ? Math.min(...deliveryTimes) : undefined,
      positiveReviews: conversations.filter((conversation) => (conversation.rating ?? 0) >= 4).length,
      negativeReviews: conversations.filter((conversation) => conversation.rating !== undefined && conversation.rating <= 2).length,
      finalScore: snapshot.score,
      finalReputation: snapshot.reputation,
    };
  }

  private setPrepRow(row: PrepRow, status: string, complete: boolean): void {
    const element = this.prepRows.get(row);
    if (!element) return;
    const statusElement = element.querySelector<HTMLElement>('[data-prep-status]');
    if (statusElement) statusElement.textContent = status;
    element.classList.toggle('mk-prep-row--complete', complete);
  }

  private orderMessage(chickens: number): string {
    const phrase = this.chickenPhrase(Math.max(1, chickens));
    return `السلام عليكم، نبي صحن مندي فيه ${phrase} لو سمحت.`;
  }

  private chickenPhrase(count: number): string {
    if (count === 1) return 'دجاجة';
    if (count === 2) return 'دجاجتين';
    return `${this.formatNumber(count)} دجاجات`;
  }

  private formatTime(seconds: number): string {
    const rounded = Math.max(0, Math.ceil(seconds));
    const minutes = Math.floor(rounded / 60);
    return `${String(minutes).padStart(2, '0')}:${String(rounded % 60).padStart(2, '0')}`;
  }

  private formatShortSeconds(seconds: number): string {
    return `${this.formatNumber(Math.max(0, Math.ceil(seconds)))}ث`;
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('ar-SA', { maximumFractionDigits: 0, useGrouping: false }).format(value);
  }

  private setText(selector: string, value: string): void {
    const element = this.root.querySelector<HTMLElement>(selector);
    if (element && element.textContent !== value) element.textContent = value;
  }

  private getElement<T extends HTMLElement>(selector: string): T {
    const element = this.root.querySelector<T>(selector);
    if (!element) throw new Error(`Game UI is missing ${selector}.`);
    return element;
  }
}
